/**
 * Sniper — pure, framework-free helpers shared by the Sniper page.
 *
 * The ARM predicate is the FROZEN contract logic (`sniper-contract.md` §counter
 * enum + arm predicate), mirrored EXACTLY from the control-plane
 * `sniper.arm.ts::isArmed` and the Rust `sniper_types.rs::is_armed`:
 *
 *     armed ⇔ count(counter, state) == floor(line)
 *
 * `line` is a half-integer (`X.5`) config-time invariant, so `count ==
 * floor(line)` means the very next increment pushes the total over the line →
 * Over resolves YES. Team-scoped counters (`team_*`) resolve `count` against
 * that team only; with no team a `team_*` count is unresolvable → NOT armed
 * (fail-closed).
 *
 * The `MatchState` these operate on is the BACKEND-AUTHORED state (the
 * `match_state` WS event + `GET /match-events/state/:fixtureId` backfill) — the
 * FE renders it and never re-derives score/corners from raw incidents. `count`
 * and `isArmed` are therefore PURE functions of that backend state, not an
 * interpretation of the incident stream.
 */

import type {
  LadderBand,
  SniperCounter,
  SniperTeam,
  SniperPeriod,
  MatchState,
  SniperMarketCandidate,
  SniperMarketDraft,
} from '../types/sniper.types';

// ─── wire period ids (Body.Period.Id) ────────────────────────────────────────

/** `MatchState.period` value while the ball is in the FIRST half. */
export const WIRE_PERIOD_FIRST_HALF = 10;
/** `MatchState.period` value while the ball is in the SECOND half. */
export const WIRE_PERIOD_SECOND_HALF = 20;

// ─── arm predicate (contract §counter enum + arm predicate) ──────────────────

/**
 * The counter value for a market in the current state. Returns `null` when the
 * count is unresolvable — a `team_*` counter with no team, or a per-half scope
 * the FE cannot compute (see the `period` param) — fail-closed.
 *
 * `period` narrows the count to a half:
 *   - `full`: the whole-match total (default, unchanged).
 *   - `first_half`: resolvable ONLY while `state.period === WIRE_PERIOD_FIRST_HALF`
 *     — during the 1st half the wire total IS the 1st-half total. Outside period
 *     10 the wire no longer carries a 1H breakdown → `null` (not armed).
 *   - `second_half`: the FE has NO first-half breakdown on the wire, so it cannot
 *     subtract the 1H total to get a 2H count → `null` (fail-closed). Authoritative
 *     2H arming lives in the Rust engine (it keeps per-period scores); the FE
 *     mirror cannot compute it and MUST NOT fabricate a wrong ARMED.
 */
export function count(
  counter: SniperCounter,
  state: MatchState,
  team?: SniperTeam | null,
  period: SniperPeriod = 'full',
): number | null {
  if (period === 'second_half') return null;
  if (period === 'first_half' && state.period !== WIRE_PERIOD_FIRST_HALF) {
    return null;
  }
  switch (counter) {
    case 'match_goals':
      return state.homeScore + state.awayScore;
    case 'match_corners':
      return state.homeCorners + state.awayCorners;
    case 'team_goals':
      if (team === 'home') return state.homeScore;
      if (team === 'away') return state.awayScore;
      return null;
    case 'both_teams_to_score':
      // Match-wide: how many DISTINCT teams have scored (0..2). Half-scoped via
      // the period gate above — during the 1st half the wire totals ARE the 1H
      // totals, so this counts teams that scored in that half.
      return (state.homeScore >= 1 ? 1 : 0) + (state.awayScore >= 1 ? 1 : 0);
    case 'first_team_to_score':
      // The named team's score, but UNRESOLVABLE (`null`) once the OTHER team
      // has scored (first-to-score is lost) or with no team → fail-closed.
      if (team === 'home') return state.awayScore >= 1 ? null : state.homeScore;
      if (team === 'away') return state.homeScore >= 1 ? null : state.awayScore;
      return null;
    case 'team_corners':
      if (team === 'home') return state.homeCorners;
      if (team === 'away') return state.awayCorners;
      return null;
    default:
      return null;
  }
}

/**
 * `armed ⇔ count == floor(line)`. Half-integer `line` is a config-time
 * invariant; this predicate uses `Math.floor(line)` and does not re-validate.
 * A `team_*` counter with no team is never armed. `period` is passed through to
 * `count`, so a `first_half` market only arms during the 1st half and a
 * `second_half` market never arms in the FE mirror (fail-closed — see `count`).
 */
export function isArmed(
  counter: SniperCounter,
  state: MatchState,
  line: number,
  team?: SniperTeam | null,
  period: SniperPeriod = 'full',
): boolean {
  const c = count(counter, state, team, period);
  if (c === null) return false;
  return c === Math.floor(line);
}

// ─── subgroup (trigger-type) ordering ────────────────────────────────────────

/** A market's trigger-type subgroup — the page groups rows under these. */
export type SniperSubgroup = 'goals' | 'corners';

/** Subgroup display order (Gol before Corner) + their Spanish labels. */
export const SNIPER_SUBGROUP_ORDER: SniperSubgroup[] = ['goals', 'corners'];

export const SNIPER_SUBGROUP_LABEL: Record<SniperSubgroup, string> = {
  goals: 'Evento: Gol',
  corners: 'Evento: Corner',
};

/** Map a counter to its trigger-type subgroup. */
export function subgroupForCounter(counter: SniperCounter): SniperSubgroup {
  // BTTS/FTTS are goal-family — their trigger event is a goal.
  return counter === 'match_goals' ||
    counter === 'team_goals' ||
    counter === 'both_teams_to_score' ||
    counter === 'first_team_to_score'
    ? 'goals'
    : 'corners';
}

/**
 * Partition a list of counter-carrying rows into ordered subgroups
 * (goals first, then corners). Empty subgroups are omitted.
 */
export function groupBySubgroup<T extends { counter: SniperCounter }>(
  rows: T[],
): Array<{ subgroup: SniperSubgroup; label: string; rows: T[] }> {
  return SNIPER_SUBGROUP_ORDER.map((subgroup) => ({
    subgroup,
    label: SNIPER_SUBGROUP_LABEL[subgroup],
    rows: rows.filter((r) => subgroupForCounter(r.counter) === subgroup),
  })).filter((g) => g.rows.length > 0);
}

// ─── formatters ──────────────────────────────────────────────────────────────

/** Short half tag for a period — `''` for full-match, `1H`/`2H` for a half. */
export function periodLabel(period: SniperPeriod): string {
  if (period === 'first_half') return '1H';
  if (period === 'second_half') return '2H';
  return '';
}

/**
 * Human label for a counter (venue-agnostic). Optionally suffixes the half tag
 * (e.g. "Match goals · 1H") when a non-full `period` is provided.
 */
export function counterLabel(
  counter: SniperCounter,
  period: SniperPeriod = 'full',
): string {
  const base = (() => {
    switch (counter) {
      case 'match_goals':
        return 'Match goals';
      case 'team_goals':
        return 'Team goals';
      case 'both_teams_to_score':
        return 'Both teams to score';
      case 'first_team_to_score':
        return 'First to score';
      case 'match_corners':
        return 'Match corners';
      case 'team_corners':
        return 'Team corners';
      default:
        return counter;
    }
  })();
  const half = periodLabel(period);
  return half ? `${base} · ${half}` : base;
}

/** Short venue chip label. */
export function venueLabel(venue: string): string {
  if (venue === 'kalshi') return 'Kalshi';
  if (venue === 'polymarket') return 'Polymarket';
  return venue;
}

/** Venue chip accent colour (Kalshi teal, Polymarket indigo). */
export function venueColor(venue: string): string {
  if (venue === 'kalshi') return '#00b8a3';
  if (venue === 'polymarket') return '#5b6ef5';
  return '#9e9e9e';
}

/** `over 2.5 (home)` style summary of a market's target (optionally per-half). */
export function targetSummary(
  counter: SniperCounter,
  line: number,
  team: SniperTeam | null,
  period: SniperPeriod = 'full',
): string {
  // BTTS/FTTS have a FORCED line the operator doesn't own — the "Over 1.5" /
  // "Over 0.5" phrasing reads oddly, so use the counter label alone.
  if (counter === 'both_teams_to_score' || counter === 'first_team_to_score') {
    const base = counterLabel(counter, period);
    return team ? `${base} · ${team}` : base;
  }
  const base = `${counterLabel(counter, period)} Over ${line}`;
  return team ? `${base} · ${team}` : base;
}

/** Dollars → cents string, e.g. 0.99 → "99¢", 0.9 → "90¢". Null → "—". */
export function priceToCents(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) return '—';
  return `${Math.round(price * 100)}¢`;
}

/** Confidence-gate 0..1 → integer percent (clamped). */
export function gatePct(gate: number | null | undefined): number {
  if (gate == null || !Number.isFinite(gate)) return 0;
  return Math.max(0, Math.min(100, Math.round(gate * 100)));
}

/**
 * Operator-entered percent (1..100) → wire confidence-gate 0..1. Inverse of
 * `gatePct`. The FE shows/edits the gate as an INTEGER PERCENT while the
 * contract field `confidenceGate` stays 0..1 — so the operator can never again
 * read "Gate 1" as "1%" (it is 1.0 = 100%). Returns `null` for anything that is
 * not a usable gate — non-numeric, ≤ 0, or > 100 — so the caller fails closed
 * (the ✓ stays disabled) instead of writing a bogus gate. A valid percent maps
 * to `pct / 100`, landing in (0, 1].
 */
export function pctToGate(pct: number): number | null {
  if (!Number.isFinite(pct)) return null;
  const rounded = Math.round(pct);
  if (rounded < 1 || rounded > 100) return null;
  return rounded / 100;
}

/**
 * The event's live state line: `{home} h–a {away} · Corners h–a · clock`.
 * Mirrors the Live-match-events header derivation; renders exactly what the
 * feed provides (never a guessed score).
 */
export function formatStateLine(
  state: MatchState,
  homeTeam: string,
  awayTeam: string,
  clockText: string,
): string {
  const score = `${homeTeam} ${state.homeScore}–${state.awayScore} ${awayTeam}`;
  const corners = `Corners ${state.homeCorners}–${state.awayCorners}`;
  return `${score} · ${corners} · ${clockText}`;
}

// ─── line validation (config-time invariant, mirrored client-side) ───────────

/** Half-integer (`X.5`) — the config-time invariant. Integer lines are rejected. */
export function isHalfInteger(line: number): boolean {
  return Number.isFinite(line) && Math.abs(line - Math.floor(line) - 0.5) < 1e-9;
}

// ─── decimal text input normalization (shared by the import dialog) ──────────

/**
 * Normalize an in-progress decimal text field so the operator can freely type
 * `0.5`, `10`, `0,95`, `2.5` (comma = Spanish locale decimal separator). Pure +
 * framework-free so it is unit-testable and reusable by the controlled
 * `DecimalField`:
 *   - comma → dot (`"0,5"` → `"0.5"`),
 *   - strip anything that is not a digit or a dot (`"abc0.5x"` → `"0.5"`),
 *   - keep only the FIRST dot and everything up to the SECOND one, dropping the
 *     rest (`"0.5.3"` → `"0.5"`).
 * An in-progress `"0."` is PRESERVED (not coerced to `"0"`), which is exactly
 * what let the trailing dot survive so `0.5` is reachable.
 */
export function normalizeDecimalInput(raw: string): string {
  const stripped = raw.replace(/,/g, '.').replace(/[^0-9.]/g, '');
  const parts = stripped.split('.');
  if (parts.length <= 2) return stripped;
  return `${parts[0]}.${parts[1]}`;
}

// ─── import candidate → editable draft ───────────────────────────────────────

/**
 * Default confidence gate for a new market. Goals arrive at 100% and corners
 * can arrive unconfirmed, so BOTH default to a fire-safe 100% (`1`) — the
 * operator then lowers/raises per market. Kept as a function (not a constant)
 * so a future per-event-type default is a one-line change.
 */
export const DEFAULT_CONFIDENCE_GATE = 1;
export function defaultConfidenceGate(): number {
  return DEFAULT_CONFIDENCE_GATE;
}

/** Sensible default sweep ceiling (worst per-contract price), dollars. */
export const DEFAULT_MAX_PRICE = 0.99;

/**
 * Default limit of the Phase-1 precautionary GTC SELL posted the moment a VAR
 * review starts on our goal, before the cancellation is confirmed.
 */
export const DEFAULT_VAR_EXIT_PRICE = 0.96;

/** Default worst per-contract price for the auto-NO period-close tail sweep. */
export const DEFAULT_NO_MAX_PRICE = 0.99;

// ─── two-phase VAR-defense defaults (mirror of the backend DTO defaults) ─────

/** Master switch for the two-phase VAR defense. Fail-safe ON. */
export const DEFAULT_VAR_DEFENSE_ENABLED = true;
/** Phase 1 — precautionary sell at the configured exit price. Fail-safe ON. */
export const DEFAULT_VAR_PREVENTIVE_EXIT_ENABLED = true;
/**
 * Sell price used once a goal cancellation is CONFIRMED (Phase 2, or any
 * other revert) — now used in more scenarios than a plain Phase-2 dump, but
 * the default value itself is unchanged. A price in (0,1], like
 * `DEFAULT_VAR_EXIT_PRICE`.
 */
export const DEFAULT_VAR_CONFIRMED_FLOOR_PRICE = 0.9;
/** All-clear rebuy if the VAR ultimately confirms the goal. Fail-safe ON. */
export const DEFAULT_VAR_REBUY_ENABLED = true;

// ─── confidence ladder defaults (mirror of the backend constants) ────────────

/** Confidence-ladder opt-in. Fail-safe OFF — the one-shot path stays the default. */
export const DEFAULT_LADDER_ENABLED = false;

/**
 * The full 3-rung TABLE this system ships with — mirrors the backend's
 * `LADDER_BAND_TEMPLATES`. A goal's LSports confidence ramps from ~12% to 100%
 * over a few seconds, so the ladder buys early and cheap, rests a sell two
 * cents above, and chases both upward as the report firms up. The last
 * CONFIGURED band is open-ended. Only rung 1 is applied by default (see
 * `DEFAULT_LADDER_BANDS`) — the popover's per-rung toggles append rung 2/3
 * from here as the operator enables them.
 */
export const LADDER_BAND_TEMPLATES: LadderBand[] = [
  { maxConfidence: 0.2, buyPrice: 0.95, sellPrice: 0.97 },
  { maxConfidence: 0.4, buyPrice: 0.96, sellPrice: 0.98 },
  { maxConfidence: 0.6, buyPrice: 0.97, sellPrice: 0.99 },
];

/**
 * The default rungs for a NEW market — rung 1 ONLY, mirroring the backend's
 * `DEFAULT_LADDER_BANDS`. V1's rollout plan is one rung active at a time.
 */
export const DEFAULT_LADDER_BANDS: LadderBand[] = [LADDER_BAND_TEMPLATES[0]];

/** Upper bound on rungs — mirrors the backend's `MAX_LADDER_BANDS`. */
export const MAX_LADDER_BANDS = 5;

/**
 * The gate a ladder market defaults to. The whole point of the ladder is to be
 * in the book while the report is still uncertain, so the 100% one-shot default
 * would skip every rung but the last.
 */
export const DEFAULT_LADDER_CONFIDENCE_GATE = 0.05;

/**
 * Dollars below our own buy vwap the Phase-1 "VAR under review" preventive
 * sell rests at when unset — mirrors the backend's
 * `DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET`.
 */
export const DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET = 0.02;

/** A fresh copy of the default rungs (never hand out the shared array). */
export function defaultLadderBands(): LadderBand[] {
  return DEFAULT_LADDER_BANDS.map((b) => ({ ...b }));
}

/**
 * The ladder's effective rungs for a persisted row: what it carries, or the
 * defaults when it predates the field / was never configured.
 */
export function effectiveLadderBands(
  bands: LadderBand[] | null | undefined,
): LadderBand[] {
  return bands && bands.length > 0 ? bands.map((b) => ({ ...b })) : defaultLadderBands();
}

/**
 * Why this ladder cannot be saved, as a short operator label — or `null` when it
 * is valid. A MIRROR of `sniper.validation.ts::validateLadder` so the ✓ is
 * disabled instead of the operator earning a 400.
 *
 * Every rule is about the ladder not trading against itself; the least obvious
 * is the last one: on a promotion the BUY is amended to the next rung's price
 * WHILE the previous rung's SELL is still resting, so a buy that crossed it
 * would trip self-trade prevention and CANCEL our own sell, leaving the position
 * long and uncovered.
 */
export function ladderInvalidReason(
  bands: LadderBand[],
  floorPrice: number | null,
  varPreventiveExitOffset?: number | null,
): string | null {
  const price = (v: number) => Number.isFinite(v) && v > 0 && v <= 1;
  if (bands.length === 0) return 'add at least one band';
  if (bands.length > MAX_LADDER_BANDS) return `at most ${MAX_LADDER_BANDS} bands`;
  for (let i = 0; i < bands.length; i += 1) {
    const b = bands[i];
    if (!price(b.maxConfidence)) return `band ${i + 1}: confidence 0–100%`;
    if (!price(b.buyPrice) || !price(b.sellPrice)) {
      return `band ${i + 1}: prices 0–1`;
    }
    if (b.sellPrice <= b.buyPrice) return `band ${i + 1}: sell must beat buy`;
    if (i === 0) continue;
    const prev = bands[i - 1];
    if (b.maxConfidence <= prev.maxConfidence) {
      return `band ${i + 1}: confidence must increase`;
    }
    if (b.buyPrice <= prev.buyPrice || b.sellPrice <= prev.sellPrice) {
      return `band ${i + 1}: prices must increase`;
    }
    if (b.buyPrice >= prev.sellPrice) {
      return `band ${i + 1}: buy would cross band ${i}'s sell`;
    }
  }
  if (floorPrice == null) return 'set a floor';
  if (!Number.isFinite(floorPrice) || floorPrice <= 0 || floorPrice >= 1) {
    return 'floor 0–1';
  }
  if (floorPrice >= bands[0].buyPrice) return 'floor must be below band 1 buy';
  // Optional — undefined/null means "use the engine default (2¢)". A sanity
  // bound only: the engine clamps the derived exit to floorPrice regardless,
  // but a value >= $1 is almost certainly a typo.
  if (varPreventiveExitOffset != null) {
    if (!Number.isFinite(varPreventiveExitOffset) || varPreventiveExitOffset <= 0) {
      return 'VAR offset must be positive';
    }
    if (varPreventiveExitOffset >= 1) return 'VAR offset must be below $1';
  }
  return null;
}

/** A one-line read-only summary for the row chip: `3 bands · floor 83¢`. */
export function ladderSummary(
  bands: LadderBand[] | null | undefined,
  floorPrice: number | null,
): string {
  const n = bands?.length ?? 0;
  return `${n} band${n === 1 ? '' : 's'} · floor ${priceToCents(floorPrice)}`;
}

/**
 * Counters whose `line` is FORCED by the contract (the operator never sets it),
 * mirroring the backend's `{ both_teams_to_score: 1.5, first_team_to_score: 0.5 }`.
 * BTTS resolves on the 2nd distinct team to score (floor(1.5)=1), FTTS on the
 * first goal of the match/team (floor(0.5)=0).
 */
export const SNIPER_FORCED_LINE: Partial<Record<SniperCounter, number>> = {
  both_teams_to_score: 1.5,
  first_team_to_score: 0.5,
};

/**
 * Build an editable draft from a scraped candidate. A guessed `counter`/`line`
 * becomes an editable default; an UNCLASSIFIED candidate (`null` guess) leaves
 * `counter`/`line` UNSET (`null`) — NEVER silently defaulted to `match_goals`,
 * which would arm/fire the market on goals. Such a draft is INCOMPLETE (see
 * `isDraftComplete`) so the operator must pick a counter before it can be
 * persisted. `enabled` defaults false (fail-safe OFF), `size` defaults 10 (a
 * sensible operator default so a classified total is addable without extra
 * typing — markets are still created DISABLED / fail-safe OFF).
 */
export function candidateToDraft(
  candidate: SniperMarketCandidate,
): SniperMarketDraft {
  // The backend nests the auto-guess under `guess: {counter, team, line}`;
  // older shapes returned them flat. Read from either so the guessed
  // counter/line ALWAYS map (otherwise every row shows unset — the operator
  // would have to re-type the line by hand).
  const g = candidate.guess ?? candidate;
  // `period` lives only on the nested `guess` shape; a flat/legacy candidate has
  // no period → default 'full' (whole match). Corners never carry a half.
  const guessedPeriod =
    'period' in g ? (g as { period?: SniperPeriod | null }).period : null;
  const guessedCounter = g.counter ?? null;
  // BTTS/FTTS carry a FORCED, operator-locked line — it ALWAYS wins over a
  // guessed line so the draft can never carry a stray value the Line field
  // (disabled for these counters) would silently misrepresent.
  const forcedLine = guessedCounter
    ? SNIPER_FORCED_LINE[guessedCounter]
    : undefined;
  return {
    venue: candidate.venue,
    marketId: candidate.marketId,
    marketLabel: candidate.marketLabel,
    counter: guessedCounter,
    team: g.team ?? null,
    period: guessedPeriod ?? 'full',
    line: forcedLine ?? g.line ?? null,
    buySide: 'over',
    size: 10,
    confidenceGate: defaultConfidenceGate(),
    maxPrice: DEFAULT_MAX_PRICE,
    varExitPrice: DEFAULT_VAR_EXIT_PRICE,
    // Auto-NO period-close tail — OFF by default (fail-safe). `noMarketId`
    // prefills from the Polymarket candidate's No token when the scraper
    // carried it; Kalshi candidates have none (native NO) → null.
    autoNoEnabled: false,
    noMaxPrice: DEFAULT_NO_MAX_PRICE,
    noMarketId: candidate.noMarketId ?? null,
    autoNoIncludeExtraTime: false,
    varDefenseEnabled: DEFAULT_VAR_DEFENSE_ENABLED,
    varPreventiveExitEnabled: DEFAULT_VAR_PREVENTIVE_EXIT_ENABLED,
    varConfirmedFloorPrice: DEFAULT_VAR_CONFIRMED_FLOOR_PRICE,
    varRebuyEnabled: DEFAULT_VAR_REBUY_ENABLED,
    // Confidence ladder — OFF by default (fail-safe). The rungs are pre-filled
    // so turning it on in the import dialog is one click, but the floor stays
    // unset on purpose: it depends on the market's pre-event price, so the
    // operator has to name it (and the draft stays incomplete until they do).
    ladderEnabled: DEFAULT_LADDER_ENABLED,
    ladderBands: defaultLadderBands(),
    floorPrice: null,
    varPreventiveExitOffset: DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
    enabled: false,
  };
}

/**
 * The first trading-critical field a draft is missing, as a short operator
 * label — or `null` when the draft is complete. Drives the "incomplete · X"
 * chip so the operator sees WHY a row can't be saved (e.g. "set a size", not a
 * silent block on the disabled Team field). Order mirrors `isDraftComplete`.
 */
export function draftIncompleteReason(draft: SniperMarketDraft): string | null {
  if (draft.counter == null) return 'pick a counter';
  if (draft.line == null || !isHalfInteger(draft.line)) {
    return 'invalid line (needs X.5)';
  }
  if (!(draft.size > 0)) return 'set a size';
  // FTTS is team-scoped (REQUIRED); BTTS is match-wide (no team). Both are
  // goal-family so the corners-non-full guard below leaves them alone.
  const teamRequired =
    draft.counter === 'team_goals' ||
    draft.counter === 'team_corners' ||
    draft.counter === 'first_team_to_score';
  if (teamRequired && draft.team == null) return 'pick a team';
  if (!teamRequired && draft.team != null) return 'remove team (match-wide)';
  // Corners are full-match only (no per-half corner feed) — reject a non-full
  // period client-side to mirror the server's 400.
  const isCorners =
    draft.counter === 'match_corners' || draft.counter === 'team_corners';
  if (isCorners && draft.period !== 'full') return 'corners are full-match only';
  if (!(draft.maxPrice > 0) || draft.maxPrice > 1) return 'max price 0–1';
  if (!(draft.varExitPrice > 0) || draft.varExitPrice > 1) {
    return 'exit price 0–1';
  }
  // Phase-2 floor is a price like varExitPrice: mirror the server's (0,1] rule so
  // a batch is never posted with a row the server would reject.
  if (!(draft.varConfirmedFloorPrice > 0) || draft.varConfirmedFloorPrice > 1) {
    return 'floor price 0–1';
  }
  if (!(draft.confidenceGate >= 0 && draft.confidenceGate <= 1)) {
    return 'confidence 0–100%';
  }
  // Auto-NO period-close tail (only validated when enabled). A Polymarket tail
  // needs its own No/Under tokenId (no native NO); Kalshi does NOT (native NO).
  // The noMaxPrice is the sweep ceiling and must be in (0,1] like maxPrice.
  if (draft.autoNoEnabled) {
    if (draft.venue === 'polymarket' && !draft.noMarketId) {
      return 'auto-NO needs a Polymarket No tokenId';
    }
    // NaN-safe, mirroring maxPrice/varExitPrice: `!(x > 0)` rejects NaN too.
    if (draft.noMaxPrice == null || !(draft.noMaxPrice > 0) || draft.noMaxPrice > 1) {
      return 'auto-NO needs a valid noMaxPrice (0,1]';
    }
  }
  // Confidence ladder (only validated when enabled — a disabled ladder may carry
  // whatever the defaults left behind, and never prices an order).
  if (draft.ladderEnabled) {
    const reason = ladderInvalidReason(draft.ladderBands, draft.floorPrice);
    if (reason) return `ladder: ${reason}`;
  }
  return null;
}

/**
 * A draft is persistable only when every trading-critical field resolves:
 * an explicit counter, a half-integer line, a positive size, a team iff the
 * counter is `team_*`, and a 0..1 gate + maxPrice/varExitPrice/varConfirmedFloorPrice
 * in (0,1].
 * Mirrors the server-side validation so the operator can't POST a guess that
 * would be rejected. Single source of truth: `draftIncompleteReason`.
 */
export function isDraftComplete(draft: SniperMarketDraft): boolean {
  return draftIncompleteReason(draft) === null;
}
